import { compareDesc, isAfter } from "date-fns";
import { meanBy } from "lodash-es";

import {
  GLOBAL_SHARED_ACCESS_ID,
  INSTRUCTOR_SHARED_ACCESS_ID,
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  groupAccessIdFromGroupId,
  isGroupAccessId,
  isStudentAccessId,
  studentAccessIdFromUserId,
} from "../../shared/access-id";
import { SectionUserActivityType } from "../../shared/types/attendance";
import { DEFAULT_CODE_KERNEL_LANG } from "../../shared/worksheets/code-kernels";

import {
  createAppSelector,
  selectActivities,
  selectAssessments,
  selectChatMessages,
  selectClassSessions,
  selectCourseCodeGroupings,
  selectCourseCodes,
  selectCourseUsers,
  selectCourses,
  selectCurrentCourseId,
  selectCurrentUserId,
  selectPageGroupUsers,
  selectPageGroups,
  selectPageOutcomes,
  selectPages,
  selectSubmissions,
  selectTerms,
  selectTopics,
} from "./base";
import { selectClassChatReadAtForCurrentCourse } from "./local";
import { selectCurrentOrgRole } from "./org";
import { selectPageById } from "./page";
import {
  Capability,
  UserRoleType,
  hasCourseCapability,
  selectCurrentUserIsMPAdmin,
  selectCurrentUserRole,
  selectUserIdsWhoCanCreateChatMessage,
} from "./permissions";
import {
  selectCourseUsersForCurrentUser,
  selectCurrentCourseUser,
  selectEnabledCourseUsers,
} from "./section-user";
import { getSortedPages, sortByTermYearRegex, sortTopicActivities } from "./sorting";
import type {
  CourseGroupingWithCoursesType,
  CourseWithGroupingType,
  EnhancedUserWithSectionActivity,
  ObjectWithCourses,
  PageWithAssessmentInfoType,
  PageWithTopicIdType,
  SectionWithAssessmentInfoType,
  SectionWithPresentationInfoType,
  UserWithSectionActivity,
  UserWithSectionUserType,
} from "./types";
import { SectionUserActivityStatus } from "./types";
import { selectAdminCourseId, selectIsStudentPreview } from "./view";

import { getTotalSubmissions } from "components/materials/page/assessment/helpers";
import type {
  ActivityType,
  CoreUserRoleType,
  CourseType,
  FullSectionType,
  PageAccessDataType,
  PageCategory,
  PageType,
  SectionRoleType,
  SectionUserType,
  TermType,
  TopicType,
  UserType,
} from "components/server-types";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { DEFAULT_ORDER_FN, ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { RootState } from "store";
import { isGroupPage, isIndividualPage, isStaticPage } from "utils/page-utils";
import {
  CONTENT_SIDEBAR_TAB_NAME,
  HomeRoleType,
  SectionPageCategoryToSidebarTab,
  courseHomeUrl,
  pageUrl,
} from "utils/urls";
import { getUserFullName, getUserShortName } from "utils/user-utils";

export const selectOrgIdForCourseId = (state: RootState, courseId: string) =>
  state.auth.course_to_org_id_map?.[courseId];

export const selectCourseById = (state: RootState, courseId: string) => state.sections[courseId];

export const selectCurrentCourse = createAppSelector(
  [selectCurrentCourseId, (state) => state.sections],
  (courseId, courses) => (courseId ? courses[courseId] : null),
);

// Selects the admin course if set, otherwise the current course.
export const selectDesignatedCourse = createAppSelector(
  [selectAdminCourseId, selectCurrentCourseId, (state) => state.sections],
  (adminCourseId, currentCourseId, courses) => {
    const courseId = adminCourseId ?? currentCourseId;
    return courseId ? courses[courseId] : null;
  },
);

export const selectOrgNameByCourseId = createAppSelector(
  [(state, courseId: string) => state.auth.course_to_org_id_map?.[courseId], (state) => state.orgs],
  (orgId, orgs) => (orgId ? orgs[orgId]?.name || "" : ""),
);

export const selectUserById = (state: RootState, userId: string | null) =>
  userId && userId in state.users ? state.users[userId] : null;

export const selectUserMap = (state: RootState) => state.users;

export const selectUserNameById = createAppSelector([selectUserById], (user) =>
  getUserFullName(user),
);

export const selectPageAccessData = createAppSelector(
  [(state) => state.page_access_data],
  (pageAccessData) => Object.values(pageAccessData),
);

export const selectPageAccessDataByPageId = createAppSelector(
  [(state, pageId: string) => pageId, selectPageAccessData],
  (pageId, pageAccessData) => pageAccessData.filter((pe) => pe.page_id === pageId),
);

export const selectPageAccessDataForAccessId = createAppSelector(
  [
    selectCourseUsers,
    (state: RootState, pageId: string) => selectPageById(state, pageId),
    (state: RootState, pageId: string) => selectPageAccessDataByPageId(state, pageId),
    (state, pageId: string, selectedAccessId: string) => selectedAccessId,
  ],
  (courseUsers, page, pageAccessData, selectedAccessId) => {
    if (!selectedAccessId || !page) {
      return null;
    }

    if (isGroupAccessId(selectedAccessId)) {
      const groupId = getGroupIdFromAccessId(selectedAccessId);
      return pageAccessData.find((pe) => pe.page_group_id === groupId);
    }

    if (isStudentAccessId(selectedAccessId)) {
      const studentId = getStudentIdFromAccessId(selectedAccessId);
      const courseUser = courseUsers.find(
        (cu) => cu.user_id === studentId && cu.section_id === page.section_id,
      );
      if (courseUser) {
        return pageAccessData.find((pe) => pe.section_user_id === courseUser.id);
      }
    }

    return pageAccessData?.[0];
  },
);

export const selectPageAccessDataForCourseUser = createAppSelector(
  [
    selectPageGroups,
    selectPageGroupUsers,
    (state, pageId: string) => selectPageById(state, pageId),
    (state: RootState, pageId: string) => selectPageAccessDataByPageId(state, pageId),
    (state, pageId: string, courseUserId: string | null) => courseUserId,
  ],
  (
    pageGroups,
    pageGroupUsers,
    page,
    pageAccessData,
    courseUserId,
  ): PageAccessDataType | undefined => {
    if (!courseUserId) {
      return undefined;
    }

    if (isGroupPage(page)) {
      const pageGroupIds = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === courseUserId && pgu.is_ready)
        .map((pgu) => pgu.page_group_id);

      const groupId = pageGroups.find(
        (pg) => pageGroupIds.includes(pg.id) && pg.page_id === page.id,
      )?.id;

      return pageAccessData.find((data) => data.page_group_id === groupId);
    }

    if (isIndividualPage(page) || isStaticPage(page)) {
      // Static pages are treated as individual pages for the purpose of marking pages complete.
      return pageAccessData.find((data) => data.section_user_id === courseUserId);
    }

    return pageAccessData?.find(
      (data) => data.page_group_id === null && data.section_user_id === null,
    );
  },
);

export const selectActivityById = (state: RootState, activityId: string) =>
  state.activities[activityId];

export const selectEnabledCourseUsersByRole = createAppSelector(
  [selectEnabledCourseUsers, (state, role?: SectionRoleType) => role],
  (courseUsers, role) => courseUsers.filter((cu) => !role || cu.role === role),
);

export const selectCurrentCourseKernelLang = (state: RootState) =>
  selectCurrentCourse(state)?.code_blocks_default_language || DEFAULT_CODE_KERNEL_LANG;

export const selectFullCoursesSorted = createAppSelector(
  [selectCourses, (state) => state.courses],
  (courses, course_codes) =>
    courses
      .map((course) => {
        const course_code = course_codes[course.course_id];

        return {
          ...course,
          full_title: t("common.course_code_and_title", {
            course_title: course.title,
            course_code_title: course_code?.title,
          }),
        } as FullSectionType;
      })
      .sort((a, b) => a.full_title.localeCompare(b.full_title)),
);

export const selectCoursesForCurrentUser = createAppSelector(
  [
    (state, role: HomeRoleType | undefined) => role,
    selectCourses,
    selectCourseUsersForCurrentUser,
    selectClassSessions,
  ],
  (role, courses, courseUsers, classSessions) => {
    const coursePresentationMap = classSessions.reduce(
      (acc, cs) => {
        if (cs.presented_page_id) {
          // eslint-disable-next-line no-param-reassign
          acc[cs.section_id] = cs.presented_page_id;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    return courses
      .filter(
        (course) =>
          role === "admin" ||
          courseUsers.some((cu) => cu.section_id === course.id && cu.role === role),
      )
      .map(
        (course) =>
          ({
            ...course,
            presented_page_id: coursePresentationMap[course.id] || null,
          }) as SectionWithPresentationInfoType,
      );
  },
);

export const selectCoursesWithAssessablePagesForCurrentUser = createAppSelector(
  [
    (state, role: HomeRoleType | undefined) => role,
    selectCourses,
    selectCourseUsersForCurrentUser,
    selectPages,
    selectPageOutcomes,
    selectSubmissions,
    selectAssessments,
    (state) => state.courses,
    selectEnabledCourseUsers,
    (state) => state.activities,
    (state) => state.topics,
    selectPageGroupUsers,
    selectPageGroups,
  ],
  (
    role,
    courses,
    courseUsersForCurrentUser,
    pages,
    pageOutcomes,
    submissions,
    assessments,
    courseCodeMap,
    enabledCourseUsers,
    activityMap,
    topicMap,
    pageGroupUsers,
    pageGroups,
  ): SectionWithAssessmentInfoType[] => {
    const coursesForRole = courses.filter(
      (course) =>
        role === "admin" ||
        courseUsersForCurrentUser.some((cu) => cu.section_id === course.id && cu.role === role),
    );

    return coursesForRole.map((course) => {
      // TODO: this should be changed to use capabilities
      const assessiblePages = pages.filter((p) => {
        if (p.section_id !== course.id) {
          return false;
        }

        // Don't show unreleased pages, unless the user is an admin or instructor
        if (!p.released_at && !["admin", "instructor"].includes(role)) {
          return false;
        }

        // Only show pages with submissions
        if (!p.submissions_last_collected_at) {
          return false;
        }

        // Students should only see pages where they have an assessed submission
        if (role === "student") {
          if (!p.assessments_published_at) {
            return false;
          }

          const myCourseUser = courseUsersForCurrentUser.find(
            (cu) => cu.section_id === course.id && cu.role === role,
          );

          if (!myCourseUser) {
            return false;
          }

          const myOfficialSubmission = submissions.find(
            (s) => s.page_id === p.id && s.section_user_id === myCourseUser.id && s.official_at,
          );

          if (!myOfficialSubmission) {
            return false;
          }

          const myAssessment = assessments.find((a) => a.submission_id === myOfficialSubmission.id);
          if (!myAssessment) {
            return false;
          }
        }

        return true;
      });

      const pagesWithAssessmentInfo: PageWithAssessmentInfoType[] = assessiblePages.map((page) => {
        const po = pageOutcomes.filter((pout) => pout.page_id === page.id);
        const submissionsForPage = submissions.filter((s) => {
          if (s.page_id !== page.id) {
            return false;
          }

          // All group submissions are included (filtering out empty groups happens in getTotalSubmissions)
          if (s.page_group_id) {
            return true;
          }

          // For individual submissions, only count those from non-disabled students
          const courseUser = enabledCourseUsers.find((cu) => cu.id === s.section_user_id);
          return courseUser && courseUser.role === "student";
        });

        // Get page group users for this page that have enabled course users
        const pageGroupsForPage = pageGroups.filter((pg) => pg.page_id === page.id);
        const pageGroupIdsForPage = new Set(pageGroupsForPage.map((pg) => pg.id));
        const enabledCourseUserIds = new Set(enabledCourseUsers.map((cu) => cu.id));
        const pageGroupUsersForPage = pageGroupUsers.filter(
          (pgu) =>
            pageGroupIdsForPage.has(pgu.page_group_id) &&
            enabledCourseUserIds.has(pgu.section_user_id),
        );

        const assessedSubmissionIds = new Set(
          submissionsForPage.filter((s) => s.official_at).map((s) => s.id),
        );

        const assessmentsForPage = assessments.filter((a) =>
          assessedSubmissionIds.has(a.submission_id),
        );

        const avgPoints = assessmentsForPage.length
          ? meanBy(assessmentsForPage, (a) => a.points)
          : undefined;

        const activity = page.activity_id ? activityMap[page.activity_id] || null : null;
        const topic = activity?.topic_id ? topicMap[activity.topic_id] || null : null;

        return {
          ...page,
          activity,
          topic,
          page_outcomes: po,
          assessible_submissions_count: getTotalSubmissions(
            page,
            submissionsForPage,
            pageGroupUsersForPage,
          ),
          assessed_submissions_count: assessmentsForPage.length,
          average_points: avgPoints,
        };
      });

      return {
        ...course,
        course_code: courseCodeMap[course.course_id],
        pages: pagesWithAssessmentInfo,
      };
    });
  },
);

export const selectCurrentCourseCodeTitle = createAppSelector(
  [selectCurrentCourse, (state) => state.courses],
  (course, courseCodes) => (course?.course_id && courseCodes[course.course_id]?.title) || null,
);

export const selectCourseCodeTitleByCourseId = createAppSelector(
  [selectCourseById, (state) => state.courses],
  (course, courseCodes) => (course.course_id && courseCodes[course.course_id]?.title) || null,
);

export const selectLmsSectionId = createAppSelector(
  [selectCurrentCourse],
  (currentCourse) => currentCourse?.lms_section_id || null,
);

export const selectCurrentCourseTitle = createAppSelector(
  [selectCurrentCourse],
  (currentCourse) => currentCourse?.title || null,
);

export const selectCourseTitleById = createAppSelector(
  [selectCourseById],
  (course) => course?.title || null,
);

export const selectCurrentCourseDescription = createAppSelector(
  [selectCurrentCourse],
  (currentCourse) => currentCourse?.description || null,
);

export const selectCurrentCourseAvEnabled = createAppSelector(
  [selectCurrentCourse],
  (currentCourse) => currentCourse?.av_enabled || false,
);

export const selectCurrentCourseAllowStudentsToJoinAv = createAppSelector(
  [selectCurrentCourse],
  (currentCourse) => currentCourse?.allow_students_to_join_av || false,
);

export const selectCourseCodeById = createAppSelector(
  [(state, courseCodeId: string) => courseCodeId, (state) => state.courses],
  (courseCodeId, codes) => codes[courseCodeId],
);

const mapUserToCourseUsers = (
  courseUsers: SectionUserType[],
  usersMap: Record<string, UserType>,
  courseId: string,
): UserWithSectionUserType[] => {
  const courseUsersWithNames = Object.values(courseUsers)
    .filter((cu) => cu.section_id === courseId)
    .map((cu) => {
      const user = usersMap[cu.user_id];
      return {
        section_user_id: cu.id,
        ...cu,
        ...user,
      };
    });

  return courseUsersWithNames;
};

// TODO: Put these two in a more appropriate place.
export const determineActivityStatus = (
  activity: SectionUserActivityType,
): SectionUserActivityStatus => {
  if (!activity) {
    return SectionUserActivityStatus.OFFLINE;
  }

  return activity.active === "0"
    ? SectionUserActivityStatus.INACTIVE
    : SectionUserActivityStatus.ACTIVE;
};

const determineCurrentPageId = (activity: SectionUserActivityType): string | null => {
  if (!activity) {
    return null;
  }

  return activity.pageId;
};

export const selectActivityStatus = (state: RootState) => state.attendance.courseUserActivities;

export const selectActivityStatusForUser = (state: RootState, userId: string) =>
  determineActivityStatus(state.attendance.courseUserActivities[userId]);

export const selectFollowingStatusForUser = (state: RootState, userId: string) =>
  state.attendance.courseUserActivities[userId]?.following === "1" || false;

export const selectActivePageIdForUser = (state: RootState, userId: string) =>
  determineCurrentPageId(state.attendance.courseUserActivities[userId]);

export const selectUsersWithCourseActivityInCurrentCourse = createAppSelector(
  [
    selectCurrentCourseId,
    selectCourseUsers,
    (state) => state.users,
    (state) => state.attendance.courseUserActivities,
  ],
  (courseId, courseUsers, users, courseUserActivities): UserWithSectionActivity[] =>
    mapUserToCourseUsers(courseUsers, users, courseId).map((user) => ({
      ...user,
      courseActivityStatus: determineActivityStatus(courseUserActivities[user.user_id]),
      isFollowing: courseUserActivities[user.user_id]?.following === "1" || false,
      pageId: determineCurrentPageId(courseUserActivities[user.user_id]),
    })),
);

export const selectUserIdsInCurrentCourse = createAppSelector(
  [selectEnabledCourseUsers, selectCurrentCourseId],
  (enabledCourseUsers, courseId) =>
    enabledCourseUsers.filter((cu) => cu.section_id === courseId).map((cu) => cu.user_id),
);

export const selectClassUserByCourseUserIdMap = createAppSelector(
  [(state) => state.class_users],
  (classUsers) =>
    Object.fromEntries(
      Object.values(classUsers).map((classUser) => [classUser.section_user_id, classUser]),
    ),
);

// NOTE: this is used in the CourseUsersSidebarPanel where we need to sort a list of users by
// course activity status and by specific fields on the ClassUser model. This could be refactored
// in the future to be more elegant/efficient, but it works for now.
export const selectEnhancedUsersWithCourseActivityInCurrentCourse = createAppSelector(
  [
    selectCurrentCourseId,
    selectCourseUsers,
    selectClassUserByCourseUserIdMap,
    (state) => state.users,
    (state) => state.attendance.courseUserActivities,
  ],
  (
    courseId,
    courseUsers,
    classUserMap,
    users,
    courseUserActivities,
  ): EnhancedUserWithSectionActivity[] =>
    mapUserToCourseUsers(courseUsers, users, courseId).map((user) => ({
      ...user,
      courseActivityStatus: determineActivityStatus(courseUserActivities[user.user_id]),
      isFollowing: courseUserActivities[user.user_id]?.following === "1" || false,
      pageId: determineCurrentPageId(courseUserActivities[user.user_id]),
      hand_raised_at: classUserMap[user.section_user_id]?.hand_raised_at || null,
      is_in_class_session: classUserMap[user.section_user_id]?.is_in_class_session || false,
    })),
);

export const selectUsersInCurrentCourse: (state: RootState) => UserWithSectionUserType[] =
  createAppSelector(
    [selectEnabledCourseUsers, (state) => state.users, selectCurrentCourseId],
    (enabledCourseUsers, users, currentCourseId): UserWithSectionUserType[] =>
      mapUserToCourseUsers(enabledCourseUsers, users, currentCourseId),
  );

export const selectUserCountInCurrentCourse = createAppSelector(
  [selectUsersInCurrentCourse],
  (users) => (users ? users.length : 0),
);

export const selectOnlineUserCountInCurrentCourse = createAppSelector(
  [selectUsersWithCourseActivityInCurrentCourse],
  (users) =>
    users?.filter((user) => user.courseActivityStatus !== SectionUserActivityStatus.OFFLINE)
      .length || 0,
);

export const selectSortedTermCoursesByCourseCode = createAppSelector(
  [(state, courseCodeId: string) => courseCodeId, selectTerms, selectCourses],
  (courseCodeId, terms, courses) => {
    const filteredCourses = courses.filter((course) => course.course_id === courseCodeId);

    const termsWithCourses = terms.map<ObjectWithCourses<TermType>>((term) => {
      const itemCourses = filteredCourses.filter((course) => course.term_id === term.id);
      const sortedCourses = itemCourses.toSorted(ORDER_BY_TITLE);
      const sortedCoursesWithOrder = sortedCourses.map((course, index) => ({
        ...course,
        order: index,
      }));
      return { ...term, courses: sortedCoursesWithOrder };
    });
    return termsWithCourses.sort(sortByTermYearRegex);
  },
);

export const selectTermById = (state: RootState, termId: string) => state.terms[termId];

export const selectCurrentTerm = createAppSelector(
  [selectCurrentCourse, (state) => state.terms],
  (currentCourse, terms) => (currentCourse?.term_id && terms[currentCourse?.term_id]) || null,
);

export const selectCurrentTermTitle = createAppSelector(
  [selectCurrentTerm],
  (currentTerm) => currentTerm?.title || null,
);

export const selectSortedCourseCodeCoursesByTerm = createAppSelector(
  [(state, termId: string) => termId, selectCourseCodes, selectCourses],
  (termId, course_codes, courses) => {
    const filteredCourses = courses.filter((course) => course.term_id === termId);

    const courseCodesWithCourses = course_codes.map<ObjectWithCourses<CourseType>>(
      (course_code) => {
        const itemCourses = filteredCourses.filter((course) => course.course_id === course_code.id);
        const sortedCourses = itemCourses.toSorted(ORDER_BY_TITLE);
        const sortedCoursesWithOrder = sortedCourses.map((course, index) => ({
          ...course,
          order: index,
        }));
        return { ...course_code, courses: sortedCoursesWithOrder };
      },
    );

    return courseCodesWithCourses.sort(ORDER_BY_TITLE);
  },
);

export type GroupingWithCourseCodesAndCoursesType = {
  id: string | null;
  title: string;
  courseCodes: ObjectWithCourses<CourseType>[];
};

/**
 * Returns groupings with their course codes and courses for a given term.
 * Includes a "fake" grouping (id: null) for ungrouped course codes.
 * All groupings, course codes, and courses are sorted by title.
 */
export const selectGroupingsWithCourseCodesByTerm = createAppSelector(
  [
    (state, termId: string) => selectSortedCourseCodeCoursesByTerm(state, termId),
    (state) => state.course_groupings,
  ],
  (courseCodesWithCourses, groupings): GroupingWithCourseCodesAndCoursesType[] => {
    // Group course codes by grouping_id
    const groupedCodes: Record<string, ObjectWithCourses<CourseType>[]> = {};
    const ungroupedCodes: ObjectWithCourses<CourseType>[] = [];

    courseCodesWithCourses.forEach((code) => {
      if (code.courses.length === 0) {
        return;
      }

      if (code.grouping_id) {
        if (!groupedCodes[code.grouping_id]) {
          groupedCodes[code.grouping_id] = [];
        }

        groupedCodes[code.grouping_id].push(code);
      } else {
        ungroupedCodes.push(code);
      }
    });

    // Build result with real groupings
    const result: GroupingWithCourseCodesAndCoursesType[] = Object.entries(groupedCodes).map(
      ([id, codes]) => ({
        id,
        title: groupings[id]?.title || "",
        courseCodes: codes.sort(ORDER_BY_TITLE),
      }),
    );

    // Add fake grouping for ungrouped course codes
    if (ungroupedCodes.length > 0) {
      result.push({
        id: null,
        title: "",
        courseCodes: ungroupedCodes.sort(ORDER_BY_TITLE),
      });
    }

    // Sort groupings by title, but keep uncategorized (null id) at the end
    return result.sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;

      // eslint-disable-next-line babel/new-cap
      return ORDER_BY_TITLE(a, b);
    });
  },
);

export const selectCourseCodeCoursesByTerms = createAppSelector(
  [selectTerms, selectCourseCodes, selectCourses],
  (terms, course_codes, courses) => {
    return terms.reduce(
      (acc, term) => {
        const filteredCourses = courses.filter((course) => course.term_id === term.id);
        const courseCodes = course_codes.map<ObjectWithCourses<CourseType>>((course_code) => {
          const itemCourses = filteredCourses
            .filter((course) => course.course_id === course_code.id)
            .map((course, index) => ({ ...course, order: index }));
          return { ...course_code, courses: itemCourses };
        });
        // eslint-disable-next-line no-param-reassign
        acc[term.id] = courseCodes;
        return acc;
      },
      {} as Record<string, ObjectWithCourses<CourseType>[]>,
    );
  },
);

export const selectCourseCodeGroupingsWithCodes = createAppSelector(
  [selectCourseCodeGroupings, selectCourseCodes],
  (groupings, courseCodes) => {
    // Create a map of grouping_id to course codes
    const codesByGrouping = new Map<string, CourseType[]>();
    courseCodes.forEach((code) => {
      if (code.grouping_id) {
        const existing = codesByGrouping.get(code.grouping_id) || [];
        codesByGrouping.set(code.grouping_id, [...existing, code]);
      }
    });

    // Map groupings to include their course codes and sort by title
    return groupings
      .map<CourseGroupingWithCoursesType>((grouping) => ({
        ...grouping,
        courseCodes: codesByGrouping.get(grouping.id)?.sort(ORDER_BY_TITLE) || [],
      }))
      .sort(ORDER_BY_TITLE);
  },
);

export const selectUngroupedCourseCodes = createAppSelector([selectCourseCodes], (courseCodes) =>
  courseCodes.filter((code) => !code.grouping_id).sort(ORDER_BY_TITLE),
);

export const selectCourseCodesByGroupingId = createAppSelector(
  [(state, groupingId: string | null) => groupingId, selectCourseCodes],
  (groupingId, courseCodes) =>
    courseCodes.filter((code) => code.grouping_id === groupingId).sort(ORDER_BY_TITLE),
);

export const selectCourseCodeGroupingById = createAppSelector(
  [(state, groupingId: string | null) => groupingId, (state) => state.course_groupings],
  (groupingId, groupings) => groupings[groupingId] || null,
);

export const selectCourseCodesWithGrouping = createAppSelector(
  [selectCourseCodes, (state) => state.course_groupings],
  (courseCodes, groupings): CourseWithGroupingType[] => {
    // Create a map of grouping_id to grouping title for quick lookup
    return courseCodes.map((code) => ({
      ...code,
      groupingTitle:
        code.grouping_id && code.grouping_id in groupings
          ? groupings[code.grouping_id].title
          : null,
    }));
  },
);

export const selectAuthorizedPages = createAppSelector(
  [
    selectPages,
    selectCurrentUserRole,
    selectEnabledCourseUsersByRole,
    selectCurrentUserId,
    selectCurrentCourseId,
    (_state, role?: CoreUserRoleType) => role,
  ],
  (pages, currentCourseUserRole, courseUsers, userId, currentCourseId, role) => {
    // TODO: make make this a helper function
    const userRole =
      role === "student"
        ? UserRoleType.STUDENT
        : role === "instructor"
          ? UserRoleType.INSTRUCTOR
          : currentCourseUserRole;

    const courseIds = role
      ? courseUsers.filter((cu) => cu.user_id === userId).map((cu) => cu.section_id)
      : [currentCourseId];

    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    return pages.filter(
      (page) => courseIds.includes(page.section_id) && (canAuthorCourse || page.released_at),
    );
  },
);

export const selectPagesForCurrentCourse = createAppSelector(
  [(state, category?: PageCategory) => category, (state) => selectAuthorizedPages(state)],
  (category, pages): PageType[] => pages.filter((p) => !category || p.category === category),
);

export const selectGroupPagesForCurrentCourse = createAppSelector(
  [selectPagesForCurrentCourse, selectActivities, selectTopics],
  (pages, activities, topics) => {
    // Filter out individual pages and only keep group pages.
    const groupPages = pages.filter((p) => isGroupPage(p));

    const topicById = topics.reduce(
      (acc, topic) => {
        return { [topic.id]: topic, ...acc };
      },
      {} as Record<string, TopicType>,
    );

    const activityById = activities.reduce(
      (acc, activity) => {
        return { [activity.id]: activity, ...acc };
      },
      {} as Record<string, ActivityType>,
    );

    return groupPages.map((page) => {
      if (page.activity_id) {
        return {
          ...page,
          topic_id: topicById[activityById[page.activity_id]?.topic_id]?.id || undefined,
        } as PageWithTopicIdType;
      }

      return page as PageWithTopicIdType;
    });
  },
);

export const selectCoursesByTermAndCourseCode = createAppSelector(
  [
    (state, termId: string | null) => termId,
    (state, termId: string | null, courseCodeId: string | null) => courseCodeId,
    selectCourses,
  ],
  (termId, courseCodeId, courses) =>
    courses
      .filter(
        (course) =>
          (!termId || course.term_id === termId) &&
          (!courseCodeId || course.course_id === courseCodeId),
      )
      .sort(ORDER_BY_TITLE),
);

export type StudentAccessIdInfoType = {
  name: string;
  accessId: string;
};

// TODO: Some of this logic is duplicated in calculateAccessId
// This function determines what access ids should be used for the current page
// If the user is a student it will return that student's access id and user name for the page.
// If the user is not a student it will determine which user has been selected or if all
// the courses users should be used and return those access ids with corresponding names.
export const selectUserDocInfoByPageId = createAppSelector(
  [
    selectPageById,
    (state, pageId: string, accessId: string) => accessId,
    (state, pageId: string, accessId: string, isAssessing: boolean) => isAssessing,
    selectPageGroups,
    selectPageGroupUsers,
    selectCurrentCourseUser,
    selectUsersInCurrentCourse,
  ],
  (
    page,
    accessId,
    isAssessing,
    pageGroups,
    pageGroupUsers,
    currentCourseUser,
    courseUsers,
  ): StudentAccessIdInfoType[] => {
    const shouldRestrictAccess = ["student", "recording_agent"].includes(currentCourseUser?.role);

    if (!page || !courseUsers?.length) {
      return null;
    }

    if (page.interaction_mode === InteractionMode.SHARED_CLASS) {
      return [{ name: t("common.shared_class"), accessId: GLOBAL_SHARED_ACCESS_ID }];
    }

    const groupsForPage = pageGroups.filter((pg) => pg.page_id === page.id);
    let groupIndex = -1;
    let userId = null;
    if (shouldRestrictAccess) {
      const groupIdsForStudent = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === currentCourseUser.id && pgu.is_ready)
        .map((pgu) => pgu.page_group_id);
      groupIndex = groupsForPage.findIndex(
        (pg) => pg.page_id === page.id && groupIdsForStudent.includes(pg.id),
      );

      if (groupIndex === -1) {
        userId = currentCourseUser.user_id;
      }

      // Here an instructor has selected a particular accessId
    } else if (accessId && accessId !== INSTRUCTOR_SHARED_ACCESS_ID) {
      if (isGroupAccessId(accessId)) {
        const groupId = getGroupIdFromAccessId(accessId);
        groupIndex = groupsForPage.findIndex((pg) => pg.id === groupId);
      } else {
        userId = getStudentIdFromAccessId(accessId);
      }
    }

    if (groupIndex > -1) {
      return [
        {
          name: t("common.group_title", { number: groupIndex + 1 }),
          accessId: groupAccessIdFromGroupId(groupsForPage[groupIndex].id),
        },
      ];
    }

    if (userId) {
      const extendedCourseUser = courseUsers.find((cu) => cu.user_id === userId);
      return extendedCourseUser
        ? [
            {
              // If we are assessing we don't want to show the user's name
              name: isAssessing ? "Anonymous Student" : getUserShortName(extendedCourseUser),
              accessId: studentAccessIdFromUserId(extendedCourseUser.user_id),
            },
          ]
        : null;
    }

    // If no user or group selected we need to return everyone
    if (groupsForPage.length) {
      return groupsForPage.map((pg, index) => ({
        name: t("common.group_title", { number: index + 1 }),
        accessId: groupAccessIdFromGroupId(pg.id),
      }));
    }

    return courseUsers
      .filter((cu) => cu.role === "student")
      .map((cu) => ({
        name: getUserShortName(cu),
        accessId: studentAccessIdFromUserId(cu.user_id),
      }));
  },
);

export const selectCoursesThatCanBeAuthored = createAppSelector(
  [
    selectIsStudentPreview,
    selectFullCoursesSorted,
    selectCourseUsersForCurrentUser,
    selectCurrentOrgRole,
    selectCurrentUserIsMPAdmin,
  ],
  (isStudentPreview, courses, courseUsers, orgRole, isMpAdmin) => {
    if (isStudentPreview) {
      return [];
    }
    return courses.filter((course) => {
      const courseUser = courseUsers.find((cu) => cu.section_id === course.id);
      if (courseUser) {
        return !["student", "recording_agent"].includes(courseUser.role);
      }
      if (orgRole) {
        return !["other", "recording_agent"].includes(orgRole);
      }
      return isMpAdmin;
    });
  },
);

// When deleting the page the user is currently on, we need to redirect to another page.
// This selector provides the URL to redirect to.
// - If the page we are deleting is part of a topic (whether it has a due date or not), we redirect to the previous
//   page in the same topic. If the page was the first page of the topic, we redirect to the next one within the same
//   topic. We try to stay within the same activity/subcategory (prep work, additional resources, etc.) but if
//   there are no other pages in that category, we will redirect to the previous or next page within the same topic.
// - If the page is an assignment (but not part of a topic), course resource or private workspace page, we redirect to
//   the previous page within the same section. If the page was the first page of the section, we redirect to the next
//   one within the same section.
// - If there are no page left within that topic or section, we redirect to the course page.
export const selectRedirectUrlForPageDeletion = createAppSelector(
  [
    (state, currentPageId: string | undefined) => currentPageId,
    (state, currentPageId: string | undefined, targetPageId: string | undefined) => targetPageId,
    selectCurrentCourseId,
    selectCurrentUserRole,
    selectPageById,
    selectActivities,
    selectPages,
  ],
  (currentPageId, targetPageId, currentCourseId, userRole, page, activities, pages) => {
    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    if (!canAuthorCourse || !currentPageId || currentPageId !== targetPageId || !page) {
      return null;
    }
    const COURSE_OVERVIEW_URL = courseHomeUrl(currentCourseId);
    let sortedPages: PageType[] = [];
    if (page.category === "activity_page") {
      const activity = activities.find((a) => a.id === page.activity_id);
      const topicId = activity?.topic_id;
      if (!topicId) {
        return COURSE_OVERVIEW_URL;
      }
      const sortedActivities = sortTopicActivities(topicId, activities, pages, canAuthorCourse);
      sortedPages = getSortedPages(sortedActivities);
    } else {
      sortedPages = pages
        .filter((p) => p.section_id === currentCourseId && p.category === page.category)
        .sort(DEFAULT_ORDER_FN);
    }
    const currentPageIndex = sortedPages.findIndex((p) => p.id === currentPageId);
    if (currentPageIndex === -1) {
      return COURSE_OVERVIEW_URL;
    }
    // If at all possible, we try to stay within the same activity/subcategory
    const previousPage = sortedPages[currentPageIndex - 1];
    const nextPage = sortedPages[currentPageIndex + 1];
    // sortedPages is either empty or only contains the current page.
    if (!previousPage && !nextPage) {
      return COURSE_OVERVIEW_URL;
    }
    // For the first page of sortedPages, we always redirect to the next page.
    if (!previousPage) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentCourseId, nextPage.id, { tab });
    }
    // For the last page of sortedPages, we always redirect to the previous page.
    if (!nextPage) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentCourseId, previousPage.id, { tab });
    }
    // If the previous page is part of the same activity/subcategory, we redirect to it.
    if (previousPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentCourseId, previousPage.id, { tab });
    }
    // If the next page is part of the same activity/subcategory, we redirect to it.
    if (nextPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentCourseId, nextPage.id, { tab });
    }
    // If we can't stay within the same activity/subcategory, we redirect to the previous page.
    const tab = SectionPageCategoryToSidebarTab[previousPage.category] as CONTENT_SIDEBAR_TAB_NAME;
    return pageUrl(currentCourseId, previousPage.id, { tab });
  },
);

// Map of course user ids to user ids
// TODO: this could be optimized since the ids should not change that often
export const selectCourseUserIdToUserIdMap = createAppSelector(
  [selectCourseUsers],
  (courseUsers) => {
    const courseUserIdToUserIdMap = new Map<string, string>();
    courseUsers.forEach((courseUser) => {
      courseUserIdToUserIdMap.set(courseUser.id, courseUser.user_id);
    });
    return courseUserIdToUserIdMap;
  },
);

export const selectUserByCourseUserId = createAppSelector(
  [
    selectCourseUserIdToUserIdMap,
    (state) => state.users,
    (state, courseUserId: string) => courseUserId,
  ],
  (courseUserIdToUserIdMap, users, courseUserId) => {
    const userId = courseUserIdToUserIdMap.get(courseUserId) || null;
    return userId && userId in users ? users[userId] : null;
  },
);

export const selectSortedChatMessagesForCurrentCourse = createAppSelector(
  [selectChatMessages, selectUserIdsWhoCanCreateChatMessage, selectCurrentCourseId],
  (chatMessages, userIdsWhoCanCreateChatMessage, currentCourseId) => {
    const currentCourseChatMessages = chatMessages.filter(
      (message) =>
        userIdsWhoCanCreateChatMessage.includes(message.author_id) &&
        message.section_id === currentCourseId,
    );
    return currentCourseChatMessages.toSorted((a, b) => compareDesc(a.created_at, b.created_at));
  },
);

export const selectUnreadChatMessageCountForCurrentCourse = createAppSelector(
  [selectSortedChatMessagesForCurrentCourse, selectClassChatReadAtForCurrentCourse],
  (chatMessages, readAt) => {
    if (!readAt) {
      return chatMessages.length;
    }
    return chatMessages.filter((message) => isAfter(message.created_at, readAt)).length;
  },
);
